import * as React from 'react'
import { ComponentProps } from 'react'
import { useTranslation } from 'react-i18next'

import { useFiltersApi } from '../../../hooks'

import { FilterProps } from '../../common/Filter/types'
import { SelectFilter } from '../../common/Filter/SelectFilter/SelectFilter'
import { Option } from '../../common/Form/types'

import { useFilterLabels } from './useFilterLabels'
import { GetAccountsRequestParams } from '@/hooks/useApi/useFiltersApi/types'
import { type Account } from '@/hooks/useApi/types'

export type AccountFilterProps = {
    name: string
    onChange: (accounts: string[]) => void
    value: string[]
    defaultValue: string[]
    disabled?: boolean
    valueLabel?: string
    params: Partial<GetAccountsRequestParams>
} & Pick<FilterProps, 'containerRef'>

export const AccountFilter: React.FC<AccountFilterProps> = (props) => {
    const { disabled, value, defaultValue, name, params, onChange } = props

    const { useAccountsQuery } = useFiltersApi()

    const query = useAccountsQuery(params)

    const { data, isLoading, isError, isFetched, isFetching } = query
    const labels = useFilterLabels()
    const { t } = useTranslation('translations')

    const optionMap = React.useMemo<Map<string, Account>>(() => {
        if (!data) return new Map<string, Account>()
        return new Map(
            data.map((account) => [account.panId.toString(), account])
        )
    }, [data])

    const options = data?.map((account) => {
        return {
            value: account.panId.toString(),
            label: account.panNumber,
        }
    })

    const renderRichLabel = React.useCallback(
        (
            option: Option,
            highlightMatchingText?: (
                input: string
            ) => string | React.JSX.Element
        ) => {
            if (typeof option.value !== 'string') return option.label

            const rawOption = optionMap.get(option.value)

            if (rawOption) {
                return (
                    <div>
                        {highlightMatchingText?.(rawOption.panNumber) ??
                            rawOption.panNumber}
                    </div>
                )
            }

            return option.label
        },
        [optionMap]
    )

    const passedProps: ComponentProps<typeof SelectFilter> = {
        name,
        disabled: !!disabled,
        onChange: onChange,
        searchConfig: {
            name: `${name}-search`,
            placeholder: t('filters.account.searchPlaceholder'),
        },
        config: {
            value: value,
            options: options,
        },
        value: value,
        defaultValue: defaultValue,
        renderRichLabel,
        label: t('filters.account.label'),
        ...labels,
        isLoading: isLoading || isFetching || !isFetched,
        isError,
        valueLabel: props.valueLabel,
        containerRef: props.containerRef,
    }

    return <SelectFilter {...passedProps} />
}
